import React, { useCallback, useEffect, useMemo, useState, memo, useRef, SyntheticEvent } from 'react'
import { useParams } from 'react-router-dom'
import { Card } from 'primereact/card'
import { createSelector, nanoid } from '@reduxjs/toolkit'
import { BaseQueryFn, TypedUseQueryStateResult } from '@reduxjs/toolkit/query/react'
import { isEqual } from 'lodash'
import { ContextMenu } from 'primereact/contextmenu'
import { MenuItem } from 'primereact/menuitem'

import getCurrentTimeZone from '../lib/getCurrentTimeZone'
import canEditChargePoint from '../lib/can-edit-charge-point'
import getApiToken from '../lib/getApiToken'
import { getConnectorSelectedCurrentLimit } from '../lib/get-connector-selected-current-limit'
import doesSupportSmartSchedule from '../lib/does-support-smart-schedule'
import isAura from '../lib/isAura'
import { isUserappDomain } from '../lib/currentDomain'
import currentUser from '../lib/currentUser'
import useText from '../common/hooks/useText'
import {
    useSmartChargingScheduleGetByChargePointQuery,
    useSmartChargingScheduleGetChargePointScheduleStatusQuery,
    useChargePointGetQuery,
    Connector,
    ChargePoint,
    useSmartChargingScheduleClearMutation,
    useSmartChargingScheduleUpdateMutation,
    SmartChargingSchedule,
} from '../common/redux/api/web-api'
import { Scheduler } from '../common/uikit/Scheduler'
import { ScheduleEntry } from '../common/uikit/Scheduler/types'
import { CHARGE_POINT_REFRESH_TIMEOUT } from '../common/constants/ChargePointConstants'
import { useToast } from '../common/uikit/ToastProvider'
import { isFetchBaseQueryError } from '../common/redux/api/fetchBaseQueryWithReauth'

import { ConnectorType } from './ConnectorSelect'
import AlertModal from './AlertModal'
import Modal from './Modal'
import ChargePointSchedulesControls from './ChargePointSchedulesControls'
import { MIN_SAFE_CURRENT, SCHUKO_CURRENT } from './CurrentSelect'
import ScheduledTimeSlotEditorModal from './ScheduledTimeSlotEditorModal'

const defaultTimeZone = getCurrentTimeZone()

export interface ChargePointSchedulesPageProps {}

const hasAccessToConnector = (connector: Connector) => {
    const user = currentUser()
    if (connector === null || connector === undefined) return false
    return (
        user?.id === undefined ||
        user?.id === null || //Admin
        connector.connectorUserId === user?.id
    )
}

const hasAccess = (chargePoint: ChargePoint): number[] => {
    const numberOfConnectorsWithAccess: number[] = []
    for (const c of chargePoint?.connectors ?? []) {
        if (hasAccessToConnector(c) && c.connectorId) {
            numberOfConnectorsWithAccess.push(c.connectorId)
        }
    }
    return numberOfConnectorsWithAccess
}

type GetSmartSchedulesWithPeriodIdsResult = TypedUseQueryStateResult<SmartChargingSchedule[], unknown, BaseQueryFn>

const selectSchedulesWithPeriodIds = createSelector(
    (result: GetSmartSchedulesWithPeriodIdsResult) => result.data,
    () => undefined,
    (data: SmartChargingSchedule[] | undefined) =>
        data?.map((schedule) => ({
            ...schedule,
            schedulePeriods: schedule.schedulePeriods?.map((period) => ({
                id: nanoid(),
                ...period,
            })),
        }))
)

export const ChargePointSchedulesPage = memo(function (): React.ReactElement | null {
    const text = useText()
    const { chargePointId } = useParams()
    const toast = useToast()
    const contextMenuRef = useRef<ContextMenu>(null)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (contextMenuRef.current && !contextMenuRef.current?.getElement()?.contains(event.target as Node)) {
                contextMenuRef.current?.hide(event as unknown as SyntheticEvent)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('touchstart', handleClickOutside)
        }
    }, [])

    const { data: chargePoint, refetch: refetchChargePoint } = useChargePointGetQuery(
        { chargePointId: chargePointId ?? '', expand: 'ocppConfig' },
        { pollingInterval: CHARGE_POINT_REFRESH_TIMEOUT }
    )
    const { data: chargePointSchedules, isLoading: isSchedulesLoading } = useSmartChargingScheduleGetByChargePointQuery(
        {
            chargePointId: chargePointId ?? '',
        },
        {
            selectFromResult: (result) => ({
                ...result,
                data: selectSchedulesWithPeriodIds(result),
            }),
        }
    )

    const { data: chargePointScheduleStatus } = useSmartChargingScheduleGetChargePointScheduleStatusQuery(
        {
            chargePointId: chargePointId ?? '',
        },
        { pollingInterval: 5000 }
    )

    const [deleteSchedule, { isLoading: deleteScheduleIsLoading }] = useSmartChargingScheduleClearMutation()
    const [updateSchedule, { isLoading: updateScheduleIsLoading }] = useSmartChargingScheduleUpdateMutation()

    const isScheduleSynced = chargePointScheduleStatus === 'Synced'
    const [showStatusIcon, setShowStatusIcon] = useState(false)
    const [selectedConnectorId, selectConnectorId] = useState(1)

    const [editModalIsVisible, setEditModalIsVisible] = useState(false)

    const [selectedSchedulePeriod, setSelectedSchedulePeriod] = useState<ScheduleEntry | null>(null)

    const isChargerOffline = chargePoint?.chargePointStatus === 'Offline'

    const checkIsChargerEditable = useCallback(() => {
        if (chargePoint?.chargePointStatus === 'Online') return Promise.resolve()
        return refetchChargePoint().then(({ data }) => {
            if (data?.chargePointStatus === 'Online') return
            return Promise.reject()
        })
    }, [chargePoint?.chargePointStatus, refetchChargePoint])

    const numberOfConnectors = useMemo(() => {
        return chargePoint?.numberOfConnectors || chargePoint?.connectors?.length || 0
    }, [chargePoint?.connectors?.length, chargePoint?.numberOfConnectors])

    useEffect(() => {
        if (isUserappDomain && chargePoint && chargePoint?.ownerId === null) {
            const numberOfConnectorsWithAccess = hasAccess(chargePoint)
            selectConnectorId(numberOfConnectorsWithAccess[0] ?? 1)
        }
    }, [chargePoint])

    const [minPossibleCurrent, maxPossibleCurrent] = useMemo(() => {
        const selectedConnector = chargePoint?.connectors?.find((c) => c.connectorId === selectedConnectorId)
        if (selectedConnector?.connectorFunctionType === 'Schuko') return [SCHUKO_CURRENT, SCHUKO_CURRENT]
        const connectorsLimit = getConnectorSelectedCurrentLimit(chargePoint?.ocppConfigKeys ?? [], selectedConnectorId)
        return [MIN_SAFE_CURRENT, connectorsLimit]
    }, [selectedConnectorId, chargePoint])

    const getDefaultSchedule = useCallback(
        () => ({
            isActive: true,
            scheduleId: undefined,
            defaultCurrent: 0,
            schedulePeriods: [],
            timeZone: defaultTimeZone,
            connectorId: selectedConnectorId,
            chargePointId,
        }),
        [chargePointId, selectedConnectorId]
    )

    const [selectedSchedule, setSelectedSchedule] = useState(
        chargePointSchedules?.find((schedule) => schedule.connectorId === selectedConnectorId) || getDefaultSchedule()
    )

    const onSchedulesChange = useCallback(
        (schedulePeriods: ScheduleEntry[]) =>
            setSelectedSchedule((selectedSchedule) => ({ ...selectedSchedule, schedulePeriods, isActive: true })),
        []
    )

    const scheduleHasChanges = useMemo(() => {
        const existingSchedule = chargePointSchedules?.find((schedule) => schedule.connectorId === selectedConnectorId)
        if (existingSchedule) {
            return !isEqual(existingSchedule, selectedSchedule)
        }

        return selectedSchedule?.schedulePeriods?.length
    }, [selectedSchedule, chargePointSchedules, selectedConnectorId])

    useEffect(() => {
        setSelectedSchedule(() => {
            return (
                chargePointSchedules?.find((schedule) => schedule.connectorId === selectedConnectorId) ||
                getDefaultSchedule()
            )
        })
    }, [chargePointSchedules, getDefaultSchedule, selectedConnectorId])

    const changeConnector = (connectorId: number) => {
        if (selectedConnectorId === connectorId) return
        if (connectorId > numberOfConnectors) return
        if (!scheduleHasChanges || confirm(text('msgChangeConnectorConfirm'))) {
            setShowStatusIcon(false)
            selectConnectorId(connectorId)
        }
    }

    useEffect(() => {
        const handler = (ev: BeforeUnloadEvent) => {
            if (!scheduleHasChanges || !getApiToken()) return
            ev.preventDefault()
            return (ev.returnValue = text('msgUnsavedChangesConfirm'))
        }
        window.addEventListener('beforeunload', handler, { capture: true })
        return () => window.removeEventListener('beforeunload', handler, { capture: true })
    }, [scheduleHasChanges, text])

    function hideSyncedIcon() {
        setTimeout(function () {
            setShowStatusIcon(false)
        }, 5000)
    }

    const saveSchedule = useCallback(() => {
        toast.notifyInfo(text('msgSavingSchedule'))

        return updateSchedule({ smartChargingSchedule: selectedSchedule })
            .unwrap()
            .then(() => {
                refetchChargePoint().then(() => {
                    setShowStatusIcon(true)
                    if (selectedConnectorId === ConnectorType.CHARGER || isAura(chargePoint)) {
                        toast.notifySuccess(text('msgSchedulesSaved'))
                    } else {
                        toast.notifySuccess(
                            text('msgSocketScheduleSaved', {
                                isCurrentLimitLowerThanDefaultCurrent:
                                    (selectedSchedule?.defaultCurrent || minPossibleCurrent) > maxPossibleCurrent,
                                connectorSelectedCurrentLimit: maxPossibleCurrent,
                            })
                        )
                    }
                })
            })
            .catch((error: unknown) => {
                if (isFetchBaseQueryError(error)) {
                    if (error.status === 410) {
                        return toast.notifyError(text('msgOfflineChargerScheduleIsNotEditable'))
                    }
                } else {
                    toast.notifyError(text('msgFailedToUpdateSchedule'))
                }
            })
    }, [
        chargePoint,
        maxPossibleCurrent,
        minPossibleCurrent,
        refetchChargePoint,
        selectedConnectorId,
        selectedSchedule,
        text,
        toast,
        updateSchedule,
    ])

    const clearSchedule = useCallback(() => {
        toast.notifyInfo(text('msgClearingSchedule'))

        setShowStatusIcon(true)
        return checkIsChargerEditable()
            .then(() => {
                deleteSchedule({ chargePointId: chargePointId ?? '', connectorId: selectedConnectorId }).unwrap()
                setSelectedSchedule(getDefaultSchedule())
            })
            .finally(() => {
                hideSyncedIcon()
            })
    }, [chargePointId, checkIsChargerEditable, deleteSchedule, getDefaultSchedule, selectedConnectorId, text, toast])

    const [currentCollisionModalVisibility, setCurrentCollisionModalVisibility] = useState(false)
    const saveIfConfirmed = useCallback(() => {
        if (!getApiToken()) {
            history.back() // will redirect user to the login page
            return
        }

        if (!selectedSchedule.schedulePeriods?.length) {
            return clearSchedule()
        }
        const hasCurrentOverlap = selectedSchedule.schedulePeriods.some(
            (period) => period.maxCurrent === selectedSchedule.defaultCurrent
        )
        if (hasCurrentOverlap) setCurrentCollisionModalVisibility(true)
        else saveSchedule()
    }, [selectedSchedule.schedulePeriods, selectedSchedule.defaultCurrent, saveSchedule, clearSchedule])

    const changeDefaultCurrent = (defaultCurrent: number) => {
        checkIsChargerEditable().then(() => {
            if (
                !isAura(chargePoint) &&
                selectedConnectorId === ConnectorType.SOCKET &&
                selectedSchedule?.schedulePeriods?.length
            ) {
                setSelectedSchedule((selectedSchedule) => ({
                    ...selectedSchedule,
                    defaultCurrent,
                    schedulePeriods: selectedSchedule?.schedulePeriods?.map((period) => ({
                        ...period,
                        maxCurrent: defaultCurrent ? 0 : SCHUKO_CURRENT,
                    })),
                }))
            } else {
                setSelectedSchedule((selectedSchedule) => ({
                    ...selectedSchedule,
                    defaultCurrent,
                }))
            }
        })
    }

    const scheduleDisabled =
        isSchedulesLoading ||
        updateScheduleIsLoading ||
        deleteScheduleIsLoading ||
        isChargerOffline ||
        !chargePoint ||
        !doesSupportSmartSchedule(chargePoint) ||
        !canEditChargePoint(chargePoint)

    const disabledClear =
        !selectedSchedule.schedulePeriods ||
        scheduleDisabled ||
        isChargerOffline ||
        !(selectedSchedule?.schedulePeriods?.length > 0 || scheduleHasChanges)

    const onEditSchedulePeriod = () => {
        if (selectedSchedulePeriod) {
            setEditModalIsVisible(true)
        }
    }

    const onDeleteSchedulePeriod = () => {
        if (selectedSchedulePeriod && selectedSchedule) {
            const filtered = selectedSchedule.schedulePeriods?.filter(
                (nextPeriod) =>
                    !(nextPeriod.from === selectedSchedulePeriod.from && nextPeriod.to === selectedSchedulePeriod.to)
            )
            setSelectedSchedule({
                ...selectedSchedule,
                schedulePeriods: filtered,
            })
        }
    }

    const contextMenuModel: MenuItem[] = [
        {
            label: text('btnEdit'),
            icon: 'pi pi-pencil',
            command: () => {
                onEditSchedulePeriod()
            },
        },
        {
            label: text('btnDelete'),
            icon: 'pi pi-trash',
            command: () => {
                onDeleteSchedulePeriod()
            },
        },
    ]

    const getWarningMessage = useCallback(
        (schedulePeriod: ScheduleEntry) => {
            if (schedulePeriod.maxCurrent === selectedSchedule.defaultCurrent) {
                return text('msgTimeRangeEqualsDefaultCurrent')
            }
            if (maxPossibleCurrent < schedulePeriod.maxCurrent) {
                if (selectedConnectorId === ConnectorType.CHARGER || isAura(chargePoint)) {
                    return text('msgChargingUpTo', { connectorSelectedCurrentLimit: maxPossibleCurrent })
                }
                return text('msgSocketMightNotBeChargingOptimally', {
                    connectorSelectedCurrentLimit: maxPossibleCurrent,
                })
            }
            return null
        },
        [chargePoint, maxPossibleCurrent, selectedConnectorId, selectedSchedule.defaultCurrent, text]
    )

    const getCustomStyle = useCallback(
        (schedulePeriod: ScheduleEntry) => {
            if (schedulePeriod.maxCurrent === selectedSchedule.defaultCurrent) {
                return { backgroundColor: 'var(--lightCoral)' }
            }
            return {}
        },
        [selectedSchedule.defaultCurrent]
    )

    const getCustomMessage = useCallback(() => {
        if (isAura(chargePoint)) {
            return selectedConnectorId === 1 ? text('lblLeftSocket') : text('lblRightSocket')
        }
        return selectedConnectorId === ConnectorType.CHARGER ? text('lblChargerOnly') : text('lblSocketOnly')
    }, [chargePoint, selectedConnectorId, text])

    const isSocketConnector = !isAura(chargePoint) && selectedConnectorId === ConnectorType.SOCKET

    const formatCurrent = useCallback(
        (maxCurrent: number) => {
            if (isSocketConnector) {
                return maxCurrent ? text('lblOn') : text('lblOff')
            }

            return maxCurrent ? text('msgAmpereValue', { current: maxCurrent }) : text('lblDisabledStatus')
        },
        [isSocketConnector, text]
    )

    const handleContextMenu = useCallback(
        (event: React.MouseEvent | React.TouchEvent, schedulePeriod: ScheduleEntry) => {
            event.preventDefault()
            event.stopPropagation()
            setSelectedSchedulePeriod(schedulePeriod)

            const nativeEvent = event.nativeEvent

            if (window.TouchEvent && nativeEvent instanceof TouchEvent && nativeEvent.changedTouches.length > 0) {
                // Touch event
                const touch = nativeEvent.changedTouches[0]

                // Create a synthetic MouseEvent
                const simulatedEvent = new MouseEvent('contextmenu', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    clientX: touch.clientX,
                    clientY: touch.clientY,
                    screenX: touch.screenX,
                    screenY: touch.screenY,
                    pageX: touch.pageX,
                    pageY: touch.pageY,
                })

                // Pass the synthetic event to the show method
                contextMenuRef.current?.show(simulatedEvent)
            } else {
                // Mouse event
                contextMenuRef.current?.show(event)
            }
        },
        []
    )

    return (
        <Card>
            {currentCollisionModalVisibility && (
                <Modal>
                    <AlertModal
                        close={() => setCurrentCollisionModalVisibility(false)}
                        message={text('msgChangeDefaultCurrent')}
                        okButtonText={text('btnOkay')}
                    />
                </Modal>
            )}

            <ChargePointSchedulesControls
                schedule={selectedSchedule}
                onUpdateSchedule={setSelectedSchedule}
                onSaveSchedule={saveIfConfirmed}
                onClearSchedule={clearSchedule}
                chargePoint={chargePoint}
                numberOfConnectors={numberOfConnectors}
                connectorId={selectedConnectorId}
                onChangeConnectorId={changeConnector}
                disabledDefaultCurrent={!selectedSchedule?.schedulePeriods?.length}
                minPossibleCurrent={minPossibleCurrent}
                maxPossibleCurrent={maxPossibleCurrent}
                disabledSave={
                    (!scheduleHasChanges && text('msgNoChangesToSave')) || isChargerOffline || !isScheduleSynced
                }
                syncStatus={chargePointScheduleStatus}
                disabledControls={scheduleDisabled}
                disabledClear={disabledClear}
                showSyncedStatusIcon={showStatusIcon}
            />

            <Scheduler
                maxCurrent={maxPossibleCurrent}
                isActive={selectedSchedule?.isActive}
                schedules={selectedSchedule?.schedulePeriods ?? []}
                onSchedulesChange={onSchedulesChange}
                disabled={scheduleDisabled}
                onContextMenu={handleContextMenu}
                getWarningMessage={getWarningMessage}
                getCustomStyle={getCustomStyle}
                getCustomMessage={getCustomMessage}
                formatCurrent={formatCurrent}
            />

            <ScheduledTimeSlotEditorModal
                visible={editModalIsVisible}
                onHide={() => setEditModalIsVisible(false)}
                selectedSchedulePeriod={selectedSchedulePeriod}
                minPossibleCurrent={minPossibleCurrent}
                maxPossibleCurrent={maxPossibleCurrent}
                onSave={(schedulePeriod) => {
                    setEditModalIsVisible(false)
                    if (selectedSchedule) {
                        setSelectedSchedule({
                            ...selectedSchedule,
                            schedulePeriods: selectedSchedule.schedulePeriods?.map((period) =>
                                period.id === schedulePeriod.id ? schedulePeriod : period
                            ),
                        })
                    }
                }}
            />

            <ContextMenu model={contextMenuModel} ref={contextMenuRef} />
        </Card>
    )
})
